import { loadFont } from "@remotion/google-fonts/Poppins";
import { AbsoluteFill, Img, OffthreadVideo, Sequence, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { ClipReelShot, clipShotFrames, isVideoShot } from "./clipReelTiming";

const { fontFamily } = loadFont("normal", { weights: ["500", "600", "700"], subsets: ["latin"] });

/**
 * `clip-reel` — a vertical reel cut from the curated gallery: real footage, hard cuts, no shot
 * longer than three seconds.
 *
 * Every other video composition here is stills under a slow zoom. That was never a choice about
 * what reads best, it was the only thing the props allowed, and it is why 1,400 curated Thailand
 * clips have never been posted (see `work/social-pipeline-gaps.md` in stippl-knowledge). A photo
 * posted as a reel is a photo, and the feeds treat it as one.
 *
 * THE THREE SECOND CAP IS THE POINT, so it is enforced here rather than trusted to the caller.
 * `seconds` is clamped to [MIN_SHOT, MAX_SHOT] per shot; a caller asking for eight seconds gets
 * three. Travel reels hold attention by cutting, and the curated clips carry a `motion` score
 * precisely so the ones that actually move can be chosen — a static clip held for three seconds is
 * the same failure as a photo.
 *
 * Duration follows the shot list via `calculateMetadata` rather than being pinned like
 * `ranked-reel`'s 900 frames. A ranked list is three to six items and the middle can divide evenly;
 * a cut is however many shots it takes, and padding or truncating to a fixed length would either
 * repeat footage or drop it. `TikTokVideoVanilla` in the trip-movie bundle has used
 * `calculateMetadata` against this same render worker for as long as the worker has existed, so the
 * caution in `RankedReel.tsx` about renderer versions does not apply.
 *
 * Copy rides on the footage, it does not get its own slate: the hook renders over the first shot
 * and the outro over the last, so no frame is spent on a title card the viewer would scroll past.
 */

// Instagram paints its own interface over the top and bottom of a reel. Same figures as
// `ranked-reel`; `bookend-reel` uses its own larger pair, which is a known discrepancy.
const SAFE_TOP = 120;
const SAFE_BOTTOM = 320;

export type ClipReelProps = {
	eyebrow: string;
	hook: string;
	shots: ClipReelShot[];
	outro: string;
	outroSub?: string;
};

// Bottom-weighted, and lighter through the middle than `ranked-reel`'s: there the photo is a
// backdrop for a 230px numeral, here the footage is the content and the wash only has to carry a
// caption.
const WASH = "linear-gradient(180deg, rgba(7,31,38,.42) 0%, rgba(7,31,38,0) 30%, rgba(7,31,38,0) 52%, rgba(7,31,38,.86) 100%)";

/**
 * Hook and outro copy sits vertically centred, where `WASH` is deliberately clear — the wash is
 * built for a caption at the foot of the frame, not for a headline in the middle of one. Without a
 * scrim of its own the outro reads fine over a dark clip and disappears over a bright one; the
 * first render of this composition put "stippl.io" in brand green over a sunlit green hillside.
 * Type has to survive footage the composition never sees, so the copy brings its own contrast.
 */
const COPY_SCRIM = "linear-gradient(180deg, rgba(7,31,38,.18) 0%, rgba(7,31,38,.68) 30%, rgba(7,31,38,.7) 72%, rgba(7,31,38,.5) 100%)";

/**
 * The footage itself.
 *
 * `OffthreadVideo`, not `Video`: it extracts frames with FFmpeg instead of Chrome's media stack.
 * The trip-movie outro learned this the hard way — `<Video>` needs HTTP Range support from the
 * host and renders hung at `delayRender` timeouts without it (`TikTokVideoVanilla.tsx`).
 *
 * `muted` is deliberate and matches the rest of the catalog: reels render silent and the track is
 * chosen in the composer at publish time, because an unlicensed one on a business account is a
 * takedown. Without this, a Pexels clip's own audio would end up in the file.
 *
 * Photos still drift, video does not — the clips were picked for having motion of their own, and a
 * Ken Burns zoom on top of a moving camera reads as a mistake.
 */
const Shot: React.FC<{ shot: ClipReelShot; frames: number }> = ({ shot, frames }) => {
	const f = useCurrentFrame();
	const { fps } = useVideoConfig();
	const cover = { width: "100%", height: "100%", objectFit: "cover" } as const;

	if (isVideoShot(shot)) {
		return (
			<AbsoluteFill style={{ overflow: "hidden" }}>
				<OffthreadVideo src={shot.src} startFrom={Math.round((shot.startAt ?? 0) * fps)} muted style={cover} />
			</AbsoluteFill>
		);
	}

	const scale = interpolate(f, [0, frames], [1.04, 1.12], { extrapolateRight: "clamp" });
	return (
		<AbsoluteFill style={{ overflow: "hidden" }}>
			<Img src={shot.src} style={{ ...cover, transform: `scale(${scale})` }} />
		</AbsoluteFill>
	);
};

/** How far through the cut we are. Progress is why people stay, same reasoning as the ranked dashes. */
const Progress: React.FC<{ done: number; total: number; frames: number }> = ({ done, total, frames }) => {
	const f = useCurrentFrame();
	const pct = ((done + Math.min(f / frames, 1)) / total) * 100;
	return (
		<div style={{ position: "absolute", top: SAFE_TOP, left: 90, right: 90, height: 5, borderRadius: 3, background: "rgba(255,255,255,.24)" }}>
			<div style={{ width: `${pct}%`, height: "100%", borderRadius: 3, background: "#4fe3b4" }} />
		</div>
	);
};

const Caption: React.FC<{ shot: ClipReelShot }> = ({ shot }) => {
	const f = useCurrentFrame();
	const { fps } = useVideoConfig();
	const rise = spring({ frame: f - 3, fps, config: { damping: 200 } });
	if (!shot.caption && !shot.note) return null;

	return (
		<div style={{ opacity: rise, transform: `translateY(${interpolate(rise, [0, 1], [26, 0])}px)` }}>
			{shot.caption ? <div style={{ fontSize: 68, fontWeight: 700, color: "#fff", letterSpacing: -1.5 }}>{shot.caption}</div> : null}
			{shot.note ? <div style={{ fontSize: 36, color: "rgba(255,255,255,.8)", marginTop: 10 }}>{shot.note}</div> : null}
		</div>
	);
};

const Hook: React.FC<{ eyebrow: string; hook: string; frames: number }> = ({ eyebrow, hook, frames }) => {
	const f = useCurrentFrame();
	const { fps } = useVideoConfig();
	const rise = spring({ frame: f - 4, fps, config: { damping: 200 } });
	// Clears before the cut so the second shot lands on clean footage rather than fighting the hook.
	const out = interpolate(f, [frames - 10, frames], [1, 0], { extrapolateLeft: "clamp" });

	return (
		<AbsoluteFill style={{ opacity: out }}>
			<AbsoluteFill style={{ background: COPY_SCRIM }} />
			<AbsoluteFill style={{ padding: `${SAFE_TOP + 60}px 90px ${SAFE_BOTTOM}px`, display: "flex", flexDirection: "column", justifyContent: "center" }}>
				<div style={{ fontSize: 34, fontWeight: 600, letterSpacing: 6, textTransform: "uppercase", color: "#ffc14a", opacity: rise }}>{eyebrow}</div>
				<div
					style={{
						fontSize: 104,
						lineHeight: 1.05,
						fontWeight: 700,
						color: "#fff",
						letterSpacing: -2.5,
						marginTop: 22,
						opacity: rise,
						transform: `translateY(${interpolate(rise, [0, 1], [42, 0])}px)`,
					}}
				>
					{hook}
				</div>
			</AbsoluteFill>
		</AbsoluteFill>
	);
};

const Outro: React.FC<{ outro: string; outroSub?: string }> = ({ outro, outroSub }) => {
	const f = useCurrentFrame();
	const { fps } = useVideoConfig();
	const rise = spring({ frame: f - 2, fps, config: { damping: 200 } });
	return (
		<AbsoluteFill>
			<AbsoluteFill style={{ background: COPY_SCRIM }} />
			<AbsoluteFill style={{ padding: `${SAFE_TOP}px 90px ${SAFE_BOTTOM}px`, display: "flex", flexDirection: "column", justifyContent: "center" }}>
				<div
					style={{
						fontSize: 88,
						lineHeight: 1.08,
						fontWeight: 700,
						color: "#fff",
						letterSpacing: -2,
						opacity: rise,
						transform: `translateY(${interpolate(rise, [0, 1], [30, 0])}px)`,
					}}
				>
					{outro}
				</div>
				{outroSub ? <div style={{ fontSize: 42, color: "rgba(255,255,255,.82)", marginTop: 22, opacity: rise }}>{outroSub}</div> : null}
				<div style={{ fontSize: 36, fontWeight: 600, letterSpacing: 3, color: "#4fe3b4", marginTop: 46, opacity: rise }}>stippl.io</div>
			</AbsoluteFill>
		</AbsoluteFill>
	);
};

export const ClipReel: React.FC<ClipReelProps> = ({ eyebrow, hook, shots, outro, outroSub }) => {
	let cursor = 0;

	return (
		<AbsoluteFill style={{ fontFamily, backgroundColor: "#071f26" }}>
			{shots.map((shot, i) => {
				const frames = clipShotFrames(shot);
				const from = cursor;
				const done = i;
				cursor += frames;
				const first = i === 0;
				const last = i === shots.length - 1;

				return (
					// Index in the key, not `src`: the same clip can legitimately appear twice in a cut
					// (an establishing shot that returns at the end), and a duplicate key drops one.
					<Sequence key={`${i}-${shot.src}`} from={from} durationInFrames={frames}>
						<Shot shot={shot} frames={frames} />
						<AbsoluteFill style={{ background: WASH }} />
						<Progress done={done} total={shots.length} frames={frames} />

						{first ? <Hook eyebrow={eyebrow} hook={hook} frames={frames} /> : null}
						{last ? <Outro outro={outro} outroSub={outroSub} /> : null}

						{!first && !last ? (
							<AbsoluteFill style={{ padding: `${SAFE_TOP}px 90px ${SAFE_BOTTOM}px`, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
								<Caption shot={shot} />
							</AbsoluteFill>
						) : null}

						{shot.credit ? (
							<div style={{ position: "absolute", left: 90, right: 90, bottom: SAFE_BOTTOM - 54, fontSize: 22, color: "rgba(255,255,255,.55)" }}>
								{shot.credit}
							</div>
						) : null}
					</Sequence>
				);
			})}
		</AbsoluteFill>
	);
};
